import { DialogComponent } from '@theme/dialog';
import { removeTrapFocus } from '@theme/focus';

/**
 * The gift quiz (snippets/dm-gift-quiz.liquid): three questions, a short beat, then the picks.
 * The snippet holds the words and the live product data; this holds the steps.
 *
 * dialog.js supplies open, close, Esc, the outside click and the scroll lock. The native dialog
 * supplies the focus trap. Answers survive closing the sheet, as in the design: opening it again
 * returns to the same step, and only Start over clears them.
 *
 * Klaviyo: the first open in a visit shows the popup in settings.dm_quiz_klaviyo_form, and the quiz
 * follows when it closes. If nothing appears within KLAVIYO_WAIT, or Klaviyo is blocked, the quiz
 * opens anyway. The 10% line only shows once Klaviyo has reported a real submit.
 */

const PICK_BEAT = 450;
const THINK_BEAT = 900;
const KLAVIYO_WAIT = 1200;
const FORM_SEEN_KEY = 'dm-quiz-form-seen';
const CODE_SENT_KEY = 'dm-quiz-code-sent';

const session = {
  /** @param {string} key */
  has(key) {
    try {
      return sessionStorage.getItem(key) === '1';
    } catch {
      return false;
    }
  },
  /** @param {string} key */
  mark(key) {
    try {
      sessionStorage.setItem(key, '1');
    } catch {}
  },
};

/**
 * @param {string} tag
 * @param {string} [className]
 * @param {string} [text]
 */
function make(tag, className, text) {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

class DmGiftQuiz extends DialogComponent {
  /** @type {any} */
  #data = null;
  #state = { step: 0, who: '', occasion: '', budget: '', picking: false, thinking: false };
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #timer;
  /** @type {Element | null} */
  #trigger = null;
  #fromKeyboard = false;
  #formSeen = session.has(FORM_SEEN_KEY);
  #codeSent = session.has(CODE_SENT_KEY);
  /** Finishes a Klaviyo wait that is still running. @type {(() => void) | null} */
  #finishForm = null;

  connectedCallback() {
    super.connectedCallback();

    this.#data = JSON.parse(this.querySelector('[data-dm-quiz-data]')?.textContent ?? '{}');

    const { dialog } = this.refs;
    dialog.addEventListener('click', this.#onClick);
    dialog.addEventListener('cancel', this.#onCancel);
    dialog.addEventListener('close', this.#onNativeClose);
    this.addEventListener('dialog:open', this.#onOpened);
    this.addEventListener('dialog:close', this.#onClosed);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    clearTimeout(this.#timer);
  }

  /**
   * Called from the page-wide click listener in the snippet.
   * @param {Element | null} trigger
   * @param {boolean} fromKeyboard
   */
  openQuiz(trigger, fromKeyboard) {
    if (this.refs.dialog.open) return;

    this.#trigger = trigger;
    this.#fromKeyboard = fromKeyboard;

    /** @type {any} */
    const drawer = trigger?.closest('header-drawer');
    if (drawer?.isOpen) {
      drawer.close();
      removeTrapFocus();
    }

    // A second tap while the popup is up goes straight to the quiz.
    if (this.#finishForm) {
      this.#finishForm();
      return;
    }

    const formId = this.dataset.klaviyoForm;
    if (!formId || this.#formSeen) {
      this.#present();
      return;
    }

    this.#formSeen = true;
    session.mark(FORM_SEEN_KEY);
    this.#openForm(formId);
  }

  #present() {
    this.#render();
    this.showDialog();
  }

  /** @param {string} formId */
  #openForm(formId) {
    /** @type {ReturnType<typeof setTimeout> | undefined} */
    let wait;

    const finish = () => {
      clearTimeout(wait);
      window.removeEventListener('klaviyoForms', onForm);
      this.#finishForm = null;
      this.#present();
    };

    /** @param {any} event */
    const onForm = (event) => {
      const detail = event.detail;
      if (!detail || String(detail.formId) !== formId) return;

      // The popup is showing, so wait for it however long they take.
      if (detail.type === 'open') clearTimeout(wait);

      if (detail.type === 'submit') {
        this.#codeSent = true;
        session.mark(CODE_SENT_KEY);
      }

      if (detail.type === 'close') finish();
    };

    this.#finishForm = finish;
    window.addEventListener('klaviyoForms', onForm);
    wait = setTimeout(finish, KLAVIYO_WAIT);

    /** @type {any} */
    const klaviyoWindow = window;
    klaviyoWindow._klOnsite = klaviyoWindow._klOnsite || [];
    klaviyoWindow._klOnsite.push(['openForm', formId]);
  }

  /* ---------- Dialog events ---------- */

  #onOpened = () => {
    this.#focusHeading();
  };

  #onClosed = () => {
    clearTimeout(this.#timer);
    this.#state.picking = false;
    this.#state.thinking = false;
    this.#restoreFocus();
  };

  /** A close request from outside the keyboard, such as the Android back gesture. */
  #onCancel = (/** @type {Event} */ event) => {
    event.preventDefault();
    this.closeDialog();
  };

  /**
   * Some close requests cannot be cancelled and skip closeDialog, which would leave the page
   * locked in place. closeDialog clears the lock before it closes, so this only acts on those.
   */
  #onNativeClose = () => {
    const { style } = document.body;
    if (style.position !== 'fixed') return;

    const scrollY = Math.abs(Number.parseInt(style.top, 10)) || 0;
    style.width = '';
    style.position = '';
    style.top = '';
    window.scrollTo({ top: scrollY, behavior: 'instant' });
    this.refs.dialog.classList.remove('dialog-closing');
    this.#onClosed();
  };

  #restoreFocus() {
    const trigger = this.#trigger;
    this.#trigger = null;
    if (!(trigger instanceof HTMLElement) || !trigger.isConnected) return;

    // A trigger inside the closed drawer cannot take focus, so its menu button does.
    const drawerButton = trigger.closest('header-drawer')?.querySelector('summary');
    if (drawerButton instanceof HTMLElement) {
      drawerButton.focus({ preventScroll: true });
      return;
    }

    trigger.focus({ preventScroll: true });
    if (document.activeElement === trigger || !this.#fromKeyboard) return;

    // The menu panel closes with the quiz open. Hand keyboard users back to its link.
    const menuLink = trigger.closest('.menu-list__list-item')?.querySelector('a');
    if (menuLink instanceof HTMLElement) menuLink.focus({ preventScroll: true });
  }

  #focusHeading() {
    const view = this.refs.dialog.querySelector('[data-dm-quiz-view]:not([hidden])');
    const heading = view?.querySelector('[data-dm-quiz-focus]');
    if (heading instanceof HTMLElement) heading.focus({ preventScroll: true });
  }

  /* ---------- Steps ---------- */

  /**
   * Handled clicks stop here: dialog.js closes the sheet on a click whose target has left the
   * dialog, and these clicks replace the very button that was pressed.
   */
  #onClick = (/** @type {Event} */ event) => {
    if (!(event.target instanceof Element)) return;

    const option = event.target.closest('[data-dm-quiz-value]');
    if (option instanceof HTMLElement) {
      event.stopPropagation();
      this.#pick(option.dataset.dmQuizValue ?? '');
      return;
    }

    const action = event.target.closest('[data-dm-quiz-action]');
    if (action instanceof HTMLElement) {
      event.stopPropagation();
      if (action.dataset.dmQuizAction === 'back') this.#back();
      if (action.dataset.dmQuizAction === 'restart') this.#restart();
    }
  };

  /** @param {string} value */
  #pick(value) {
    clearTimeout(this.#timer);

    const state = this.#state;
    const { step } = state;
    /** @type {any} */ (state)[this.#data.questions[step].key] = value;
    state.picking = true;

    // The beat before the results is also the time to fetch their photos.
    if (step === 2) {
      this.#picks().forEach((/** @type {any} */ pick) => {
        if (pick.src) new Image().src = pick.src;
      });
    }

    const tiles = this.#find('[data-dm-quiz-tiles]');
    tiles.classList.add('is-picking');
    tiles.querySelectorAll('[data-dm-quiz-value]').forEach((tile) => {
      tile.setAttribute('aria-pressed', String(/** @type {HTMLElement} */ (tile).dataset.dmQuizValue === value));
    });
    this.#renderTray();

    this.#timer = setTimeout(() => {
      state.step = step + 1;
      state.picking = false;
      state.thinking = step === 2;
      this.#render();
      this.#focusHeading();

      if (step === 2) {
        this.#timer = setTimeout(() => {
          state.thinking = false;
          this.#render();
          this.#focusHeading();
        }, THINK_BEAT);
      }
    }, PICK_BEAT);
  }

  #back() {
    clearTimeout(this.#timer);
    this.#state.step -= 1;
    this.#state.picking = false;
    this.#state.thinking = false;
    this.#render();
    this.#focusHeading();
  }

  #restart() {
    clearTimeout(this.#timer);
    this.#state = { step: 0, who: '', occasion: '', budget: '', picking: false, thinking: false };
    this.#find('[data-dm-quiz-stack]').replaceChildren();
    this.#render();
    this.#focusHeading();
  }

  /* ---------- Rendering ---------- */

  /** @param {string} selector */
  #find(selector) {
    return /** @type {HTMLElement} */ (this.refs.dialog.querySelector(selector));
  }

  /** @param {string} name */
  #clone(name) {
    const template = /** @type {HTMLTemplateElement} */ (
      this.refs.dialog.querySelector(`template[data-dm-quiz-template="${name}"]`)
    );
    return template.content.cloneNode(true);
  }

  #answered() {
    const state = /** @type {any} */ (this.#state);
    return this.#data.questions
      .map((/** @type {any} */ question) =>
        question.answers.find((/** @type {any} */ answer) => answer.value === state[question.key])
      )
      .filter(Boolean);
  }

  #render() {
    const { step, thinking } = this.#state;
    const view = step < 3 ? 'ask' : thinking ? 'thinking' : 'results';

    this.refs.dialog.querySelectorAll('[data-dm-quiz-view]').forEach((panel) => {
      /** @type {HTMLElement} */ (panel).hidden = /** @type {HTMLElement} */ (panel).dataset.dmQuizView !== view;
    });

    if (view === 'ask') this.#renderQuestion();
    if (view === 'thinking') this.#renderThinking();
    if (view === 'results') this.#renderResults();

    this.refs.dialog.scrollTop = 0;
  }

  #renderQuestion() {
    const state = /** @type {any} */ (this.#state);
    const { step } = state;
    const question = this.#data.questions[step];

    const progress = this.#find('[data-dm-quiz-progress]');
    progress.setAttribute('aria-label', `Question ${step + 1} of 3`);
    [...progress.children].forEach((item, index) => {
      item.classList.toggle('is-reached', index <= step);
      item.classList.toggle('is-current', index === step);
      if (index === step) item.setAttribute('aria-current', 'step');
      else item.removeAttribute('aria-current');
    });

    const title = make('h2', 'dm-quiz__title', question.q);
    title.tabIndex = -1;
    title.setAttribute('data-dm-quiz-focus', '');
    const head = make('div', 'dm-quiz__head-in');
    head.append(title, make('p', 'dm-quiz__hint', question.hint));
    this.#find('[data-dm-quiz-head]').replaceChildren(head);

    const tiles = this.#find('[data-dm-quiz-tiles]');
    tiles.classList.toggle('dm-quiz__tiles--two', question.answers.length === 2);
    tiles.classList.toggle('is-picking', state.picking);
    tiles.replaceChildren(
      ...question.answers.map((/** @type {any} */ answer) => this.#tile(answer, state[question.key] === answer.value))
    );

    this.#renderTray();
    this.#find('[data-dm-quiz-action="back"]').hidden = step === 0;
  }

  /**
   * @param {any} answer
   * @param {boolean} pressed
   */
  #tile(answer, pressed) {
    const button = make('button', 'dm-quiz__opt');
    button.setAttribute('type', 'button');
    button.setAttribute('aria-pressed', String(pressed));
    button.dataset.dmQuizValue = answer.value;

    const inner = make('span', 'dm-quiz__opt-in');
    const tile = make('span', 'dm-quiz__tile');
    tile.append(this.#art(answer), this.#check());
    inner.append(tile, make('span', 'dm-quiz__opt-name', answer.label));
    if (answer.sub) inner.append(make('span', 'dm-quiz__opt-sub', answer.sub));
    button.append(inner);

    return button;
  }

  /** @param {any} answer */
  #art(answer) {
    if (answer.icon) return this.#clone(answer.icon);

    const image = /** @type {HTMLImageElement} */ (make('img'));
    image.alt = '';
    image.decoding = 'async';
    if (answer.src) image.src = answer.src;
    return image;
  }

  #check() {
    const check = make('span', 'dm-quiz__check');
    check.setAttribute('aria-hidden', 'true');
    check.append(this.#clone('tick'));
    return check;
  }

  /** @param {any} answer */
  #thumb(answer) {
    const thumb = make('span', 'dm-quiz__thumb');
    thumb.dataset.dmQuizThumb = answer.value;
    const art = make('span', 'dm-quiz__thumb-art');
    art.append(this.#art(answer));
    thumb.append(art, this.#check());
    return thumb;
  }

  /**
   * The tray keeps the thumbs it already has, so only a new answer drops in. Going back and
   * changing an answer swaps that thumb's picture where it sits.
   */
  #renderTray() {
    const answered = this.#answered();
    const stack = this.#find('[data-dm-quiz-stack]');
    const thumbs = [...stack.querySelectorAll('.dm-quiz__thumb')];
    stack.querySelectorAll('.dm-quiz__slot').forEach((slot) => slot.remove());

    answered.forEach((/** @type {any} */ answer, /** @type {number} */ index) => {
      const existing = /** @type {HTMLElement | undefined} */ (thumbs[index]);
      if (!existing) {
        stack.append(this.#thumb(answer));
      } else if (existing.dataset.dmQuizThumb !== answer.value) {
        existing.dataset.dmQuizThumb = answer.value;
        existing.querySelector('.dm-quiz__thumb-art')?.replaceChildren(this.#art(answer));
      }
    });
    thumbs.slice(answered.length).forEach((thumb) => thumb.remove());

    for (let index = answered.length; index < 3; index += 1) {
      const slot = make('span', 'dm-quiz__slot');
      slot.setAttribute('aria-hidden', 'true');
      stack.append(slot);
    }

    this.#find('[data-dm-quiz-line]').textContent = answered.length
      ? answered.map((/** @type {any} */ answer) => answer.label).join(' · ')
      : this.#data.emptyTray;
  }

  #renderThinking() {
    this.#find('[data-dm-quiz-bob]').replaceChildren(
      ...this.#answered().map((/** @type {any} */ answer) => this.#thumb(answer))
    );
  }

  /** The path's picks, without any that are unavailable or have priced out of the band. */
  #picks() {
    const { who, occasion, budget } = this.#state;
    const data = this.#data;
    const [lowest, highest] = data.bandsCents[budget] ?? [0, Infinity];

    return (data.paths[`${who}|${occasion}|${budget}`] ?? [])
      .map((/** @type {string} */ key) => data.products[key] && { ...data.products[key], ...data.copy[key] })
      .filter(
        (/** @type {any} */ pick) => pick && (!data.checkBands || (pick.cents >= lowest && pick.cents <= highest))
      );
  }

  #renderResults() {
    const { who, occasion, budget } = this.#state;
    const data = this.#data;
    const picks = this.#picks();
    const count = picks.length === 1 ? '1 gift' : `${picks.length} gifts`;

    this.#find('[data-dm-quiz-result]').textContent = `${count} for ${data.whoWords[who] ?? ''}`;
    this.#find('[data-dm-quiz-recap]').textContent = `${data.occasionWords[occasion] ?? ''}, ${(
      data.budgetWords[budget] ?? ''
    ).toLowerCase()}.`;
    this.#find('[data-dm-quiz-picks]').replaceChildren(
      ...picks.map((/** @type {any} */ pick, /** @type {number} */ index) => this.#pickRow(pick, index === 0))
    );

    const lines = [];
    if (this.#codeSent) lines.push(data.codeLine);
    if (occasion === 'christmas' && data.dateLine) lines.push(data.dateLine);
    lines.push(data.giftNote);
    this.#find('[data-dm-quiz-note]').textContent = lines.join(' ');
  }

  /**
   * @param {any} pick
   * @param {boolean} isTop
   */
  #pickRow(pick, isTop) {
    const row = make('div', 'dm-quiz__pick');

    const tile = make('span', 'dm-quiz__tile');
    const image = /** @type {HTMLImageElement} */ (make('img'));
    image.alt = pick.name;
    image.decoding = 'async';
    image.width = 96;
    image.height = 96;
    if (pick.src) image.src = pick.src;
    tile.append(image);

    const info = make('div', 'dm-quiz__pick-info');
    if (isTop) info.append(make('span', 'dm-quiz__pick-top', this.#data.topPick));
    info.append(
      make('span', 'dm-quiz__pick-name', pick.name),
      make('span', 'dm-quiz__pick-price', pick.price),
      make('span', 'dm-quiz__pick-why', pick.why)
    );

    const link = /** @type {HTMLAnchorElement} */ (make('a', 'dm-btn dm-btn--outline', pick.action));
    link.href = pick.url;
    link.setAttribute('aria-label', `${pick.action}: ${pick.name}`);
    info.append(link);

    row.append(tile, info);
    return row;
  }
}

if (!customElements.get('dm-gift-quiz')) customElements.define('dm-gift-quiz', DmGiftQuiz);
